import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bot, Users, Loader2, Search } from 'lucide-react';
import BotConfigPanel from '../shared/BotConfigPanel';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Bot Studio : configuration simplifiée de l'agent IA
// de chaque page + autorisation d'auto-configuration du client
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function BotStudio({ initialClientId = '' }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialClientId || '');
  const [selfConfigAllowed, setSelfConfigAllowed] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const linkedClients = useMemo(() => clients.filter(
    c => c.status === 'active' && c.facebook_page_id && c.page_access_token
  ), [clients]);
  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return linkedClients;
    return linkedClients.filter(c => (c.facebook_page_name || '').toLowerCase().includes(q));
  }, [linkedClients, search]);

  const loadClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients`, { headers: authHeaders() });
      const data = await res.json();
      const list = data.clients || [];
      setClients(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  useEffect(() => {
    const c = clients.find(c => c.$id === selectedClient);
    setSelfConfigAllowed(!!c?.self_config_allowed);
  }, [selectedClient, clients]);

  const toggleSelfConfig = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/config/${selectedClient}/allow-self-config`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSelfConfigAllowed(data.self_config_allowed);
        setClients(prev => prev.map(c => c.$id === selectedClient ? { ...c, self_config_allowed: data.self_config_allowed } : c));
      }
    } finally { setToggling(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
          <Bot className="w-8 h-8 text-blue-400" />
          Bot Studio
        </h1>
        <p className="text-sm text-blue-200">
          Configurez l'agent IA de chaque page en quelques minutes — identité, produits, documents (OCR), transfert humain
        </p>
      </div>

      {/* Sélecteur de page + autorisation client */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-4">
        <div>
          <label className="text-gray-400 text-xs block mb-2">Page Facebook (client)</label>
          {loading ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : (
            <div className="space-y-2 w-full md:w-96">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher une page par nom..."
                  className="w-full bg-slate-900 text-white border border-white/20 rounded-xl pl-9 pr-3 py-2 text-sm placeholder-gray-500"
                />
              </div>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full bg-slate-900 text-white border border-white/20 rounded-xl px-4 py-2.5 text-sm"
              >
                <option value="">— Choisir une page ({filteredClients.length}) —</option>
                {filteredClients.map(c => (
                  <option key={c.$id} value={c.$id}>{c.facebook_page_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {selectedClient && (
          <div className="flex items-center justify-between gap-4 p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-purple-300" />
              <div>
                <div className="text-white text-sm font-medium">Auto-configuration par le client</div>
                <div className="text-gray-500 text-xs">
                  {selfConfigAllowed
                    ? 'Le client peut configurer son agent lui-même depuis son espace « Page Facebook »'
                    : 'Seule l\'équipe TeckiA (vous) peut configurer cet agent'}
                </div>
              </div>
            </div>
            <button
              onClick={toggleSelfConfig}
              disabled={toggling}
              className={`relative w-12 h-6 rounded-full transition-all flex-shrink-0 ${
                selfConfigAllowed ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                selfConfigAllowed ? 'left-6' : 'left-0.5'
              }`} />
            </button>
          </div>
        )}
      </div>

      {/* Panneau de configuration */}
      {selectedClient && <BotConfigPanel key={selectedClient} clientId={selectedClient} />}
    </div>
  );
}
